/**
 * What the inspector is describing: a template from the library, or a client's own plan.
 *
 * The two scopes list different things and the pane beside them shows the SAME six sections about
 * both — hero, pattern week, muscle split, strip. What differs is where three facts come from and
 * what the third figure and the strip say, so the pane takes one discriminated value rather than a
 * row plus a boolean: a boolean would let a caller hand it a template and claim it was a client's
 * plan, and the branch that narrows the type is then the same branch that decides the copy.
 *
 * ── `planId` is the ONE thing every caller needs and neither row spells the same way ─────────
 * A template's plan id is `row.id`; a client's plan is the FORK, `row.planTemplateId`, and never the
 * `sourceTemplateId` it came from. Both the inspector's read, its prefetch, its `Edytuj` link and
 * the list's own row link resolve it, so it is resolved once here rather than at four call sites
 * where the fourth one gets it wrong.
 *
 * Pure. No React, no DOM.
 */

import type { ClientPlanRow } from './clientPlanRow';
import type { PlanLibraryRow } from './planLibraryRow';

export type InspectorSubject =
  | { readonly kind: 'template'; readonly row: PlanLibraryRow; readonly planId: number }
  | { readonly kind: 'clientPlan'; readonly row: ClientPlanRow; readonly planId: number };

export function templateSubject(row: PlanLibraryRow): InspectorSubject {
  return { kind: 'template', row, planId: row.id };
}

/** The client's FORK. Opening the source template would show a different document under their name. */
export function clientPlanSubject(row: ClientPlanRow): InspectorSubject {
  return { kind: 'clientPlan', row, planId: row.planTemplateId };
}
